import React from 'react';
import { Platform, StyleSheet, View, type GestureResponderEvent } from 'react-native';
import { fpColors } from '../../../styles/theme';
import { useBoxDraft, type PointerEventLike } from '../hooks/useBoxDraft';
import type { Bbox, ImageTransform } from '../useImageTransform';

/**
 * Full-stage pointer catcher for manual-add (Chunk 4).
 *
 * When `active` is true the overlay covers the whole stage. A
 * pointerdown starts a draft bbox; move resizes; up either commits
 * the final bbox (meaningful area) or cancels silently (stray tap).
 *
 * The visual is a dashed primary rectangle + a dimmed surround so
 * the user's eye tracks to the new box.
 *
 * Reuses the same `touch-action: none` trick as RefineOverlay so
 * iOS Safari doesn't hijack vertical drags for page scroll.
 */

interface DrawNewItemOverlayProps {
  active: boolean;
  transform: ImageTransform;
  containerRef: React.RefObject<HTMLElement | null>;
  /** Fires once with the image-space bbox + gesture telemetry when
   *  the user releases a meaningful-size draft. */
  onCommit: (
    bbox: Bbox,
    meta: { draw_duration_ms: number; device_kind: 'mouse' | 'touch' | 'pen' },
  ) => void;
  /** Fires if the gesture cancels (too small / escape / cancel).
   *  When `onTap` is set, too-small gestures under the tap threshold
   *  go to `onTap` instead. */
  onCancel?: (reason: 'too-small' | 'escape' | 'pointer-cancel') => void;
  /** AI tap-add (#411). Fires for a short tap with no drag; caller
   *  is expected to manufacture a centred square bbox around the
   *  anchor and POST it to `/inventory/manual-analyze`. */
  onTap?: (
    anchor: { x: number; y: number },
    meta: { draw_duration_ms: number; device_kind: 'mouse' | 'touch' | 'pen' },
  ) => void;
  /** Fires when the user taps in the letterbox area (outside the
   *  natural image bounds). Callers should toast the user to aim
   *  at the photo. */
  onTapMiss?: () => void;
}

export const DrawNewItemOverlay: React.FC<DrawNewItemOverlayProps> = ({
  active,
  transform,
  containerRef,
  onCommit,
  onCancel,
  onTap,
  onTapMiss,
}) => {
  const draft = useBoxDraft(transform, containerRef, {
    onCommit,
    onCancel,
    onTap,
    onTapMiss,
  });

  if (!active || !transform.ready) return null;

  const rect = draft.draft ? transform.toDomBbox(draft.draft) : null;

  const onPointerDown = (e: GestureResponderEvent | any) => {
    const pe: PointerEventLike = {
      clientX: e.nativeEvent?.clientX ?? e.clientX,
      clientY: e.nativeEvent?.clientY ?? e.clientY,
      pointerId: e.nativeEvent?.pointerId ?? e.pointerId ?? 1,
      pointerType: e.nativeEvent?.pointerType ?? e.pointerType,
      currentTarget: e.currentTarget,
    };
    draft.startDraw(pe);
    if (typeof (e as any).stopPropagation === 'function') {
      (e as any).stopPropagation();
    }
  };

  return (
    <View pointerEvents="box-none" style={StyleSheet.absoluteFill}>
      {/* Full-area capture surface. Dimmed so it reads as a
          distinct mode, not just another bbox overlay. */}
      <View
        {...({
          onPointerDown,
          style: [
            StyleSheet.absoluteFill,
            styles.capture,
            { touchAction: 'none' },
          ],
        } as any)}
      />

      {/* Live draft rectangle. */}
      {rect ? (
        <View
          pointerEvents="none"
          style={[
            styles.draftRect,
            {
              left: rect.x,
              top: rect.y,
              width: rect.w,
              height: rect.h,
            },
          ]}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  capture: {
    backgroundColor: 'rgba(31, 26, 34, 0.22)',
    cursor: 'crosshair',
  } as any,
  draftRect: {
    position: 'absolute',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: fpColors.primary,
    backgroundColor: `${fpColors.primary}1f`,
    borderRadius: 4,
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: `0 0 0 2px rgba(255,255,255,0.6)`,
        } as any)
      : null),
  },
});
